import {
  createServerAiBuilderChatEventMapperState,
  mapServerAiBuilderChatEvent,
  type ServerAiBuilderChatEventMapperState,
} from './serverAiBuilderChatEvents';
import {
  createSession as createServerAiBuilderChatSession,
  registerSource as registerServerAiBuilderChatSource,
  resetSession as resetServerAiBuilderChatSession,
  type ServerAiBuilderChatTransportDependencies,
  type StreamServerAiBuilderChatTurnRequest,
  streamTurn as streamServerAiBuilderChatTurn,
} from './serverAiBuilderChatTransport';
import {
  type AiBuilderChatAdapter,
  type AiBuilderChatOrchestrationEvent,
  type AiBuilderCreateSessionRequest,
  type AiBuilderCreateSessionResult,
  type AiBuilderSourceRegistrationRequest,
  type AiBuilderSourceRegistrationResult,
  type AiBuilderTurnRequest,
} from './types';

export type ServerAiBuilderChatAdapterDependencies = {
  createSession?: typeof createServerAiBuilderChatSession;
  streamTurn?: typeof streamServerAiBuilderChatTurn;
  resetSession?: typeof resetServerAiBuilderChatSession;
  registerSource?: typeof registerServerAiBuilderChatSource;
  transportDependencies?: ServerAiBuilderChatTransportDependencies;
};

export const createServerAiBuilderChatAdapter = (
  dependencies: ServerAiBuilderChatAdapterDependencies = {},
): AiBuilderChatAdapter => {
  return {
    createSession: request =>
      createServerAiBuilderChatSessionWithDependencies(request, dependencies),
    startTurn: request =>
      createServerAiBuilderChatTurnEvents(request, dependencies),
    resetSession: async request => {
      await resolveDependency(
        dependencies.resetSession,
        resetServerAiBuilderChatSession,
      )(request, dependencies.transportDependencies);
    },
    registerSource: request =>
      registerServerAiBuilderChatSourceWithDependencies(request, dependencies),
  };
};

export const serverAiBuilderChatAdapter = createServerAiBuilderChatAdapter();

const createServerAiBuilderChatSessionWithDependencies = async (
  request: AiBuilderCreateSessionRequest,
  dependencies: ServerAiBuilderChatAdapterDependencies,
): Promise<AiBuilderCreateSessionResult> => {
  const session = await resolveDependency(
    dependencies.createSession,
    createServerAiBuilderChatSession,
  )(request, dependencies.transportDependencies);

  return {
    sessionId: session.session_id,
    usage: {
      state: session.usage.state,
      remaining: session.usage.remaining,
      limit: session.usage.limit,
    },
  };
};

async function* createServerAiBuilderChatTurnEvents(
  request: AiBuilderTurnRequest,
  dependencies: ServerAiBuilderChatAdapterDependencies,
): AsyncIterable<AiBuilderChatOrchestrationEvent> {
  const session = await resolveSession(request, dependencies);

  if (session.created) {
    yield {
      type: 'setSessionId',
      sessionId: session.sessionId,
    };
    yield {
      type: 'setUsage',
      usage: session.usage,
    };
  }

  const rawEventStream = await resolveDependency(
    dependencies.streamTurn,
    streamServerAiBuilderChatTurn,
  )(
    createStreamTurnRequest(request, session.sessionId),
    dependencies.transportDependencies,
  );
  let mapperState: ServerAiBuilderChatEventMapperState =
    createServerAiBuilderChatEventMapperState();

  for await (const rawEvent of rawEventStream) {
    const result = mapServerAiBuilderChatEvent(rawEvent, {
      copy: request.clientCopy,
      requestId: request.requestId,
      state: mapperState,
    });

    mapperState = result.state;

    for (const event of result.events) {
      yield event;
    }
  }
}

const resolveSession = async (
  request: AiBuilderTurnRequest,
  dependencies: ServerAiBuilderChatAdapterDependencies,
): Promise<
  | {
      created: false;
      sessionId: string;
    }
  | {
      created: true;
      sessionId: string;
      usage: Extract<
        AiBuilderChatOrchestrationEvent,
        { type: 'setUsage' }
      >['usage'];
    }
> => {
  if (request.context.sessionId !== null) {
    return {
      created: false,
      sessionId: request.context.sessionId,
    };
  }

  const session = await resolveDependency(
    dependencies.createSession,
    createServerAiBuilderChatSession,
  )(
    {
      locale: request.context.locale,
      countryCode: request.context.countryCode,
      draftInstanceId: request.context.draftInstanceId,
    },
    dependencies.transportDependencies,
  );

  return {
    created: true,
    sessionId: session.session_id,
    usage: {
      state: session.usage.state,
      remaining: session.usage.remaining,
      limit: session.usage.limit,
    },
  };
};

const createStreamTurnRequest = (
  request: AiBuilderTurnRequest,
  sessionId: string,
): StreamServerAiBuilderChatTurnRequest => {
  const baseRequest = {
    sessionId,
    draftInstanceId: request.context.draftInstanceId,
    clientRequestId: request.clientRequestId,
    questions: request.context.questions,
    questionListRevision: request.context.questionListRevision,
    sourceRefs: request.context.sourceRefs,
    ...(request.context.signal ? { signal: request.context.signal } : {}),
    builderContext: request.context.builderContext,
    ...(request.context.selectedQuestionContext
      ? { selectedQuestionContext: request.context.selectedQuestionContext }
      : {}),
    ...(request.context.turnHints
      ? { turnHints: request.context.turnHints }
      : {}),
  };

  if (request.intent.type === 'message') {
    return {
      ...baseRequest,
      message: request.intent.message,
    };
  }

  if (request.intent.type === 'confirm') {
    return {
      ...baseRequest,
      confirm: {
        token: request.intent.token,
        decision: request.intent.decision,
      },
    };
  }

  return {
    ...baseRequest,
    action: 'undo',
  };
};

const registerServerAiBuilderChatSourceWithDependencies = async (
  request: AiBuilderSourceRegistrationRequest,
  dependencies: ServerAiBuilderChatAdapterDependencies,
): Promise<AiBuilderSourceRegistrationResult> => {
  const response = await resolveDependency(
    dependencies.registerSource,
    registerServerAiBuilderChatSource,
  )(request, dependencies.transportDependencies);

  return {
    sourceRef: response.source_ref,
    sourceType: response.source_type,
    title: response.title,
    status: response.status,
  };
};

const resolveDependency = <Dependency>(
  dependency: Dependency | undefined,
  fallback: Dependency,
) => {
  return dependency ?? fallback;
};
